import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { UpgradeDialogSource } from "schemas/dashboard";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { fetcherAuth } from "utils/api";

import { GooglePlaceBrand } from "../schema";
import { PendingItem } from "./PendingItem";
import { BrandStatus, BrandVerificationData } from "./schema";

interface IProps {
  googlePlaceBrands: GooglePlaceBrand[];
  setGooglePlaceBrands: Dispatch<SetStateAction<GooglePlaceBrand[]>>;
  open: boolean;
  searchQuery: string;
  handleClose: () => void;
  handleBack: () => void;
  openCompleted: (campaignId: number) => void;
}

type ConfirmDialogData = {
  header: string;
  subheader: string;
  confirmText: string;
  confirm: () => void;
  cancelText?: string;
  cancel?: () => void;
};

export default function BrandVerificationDialog({
  googlePlaceBrands,
  setGooglePlaceBrands,
  open,
  searchQuery,
  handleClose,
  handleBack,
  openCompleted,
}: IProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);
  const { setOpenIntegrationDialog } = useContext(UserIntegrationsContext);

  const { currentOrg } = useContext(OrganizationUserContext);

  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [nextLoading, setNextLoading] = useState(false);
  const [brandsError, setBrandsError] = useState("");
  const [brandVerifications, setBrandVerifications] = useState<
    BrandVerificationData[]
  >([]);
  const [confirmDialogData, setConfirmDialogData] =
    useState<ConfirmDialogData | null>(null);

  useEffect(() => {
    if (open && googlePlaceBrands.length > 0) {
      initializeBrandVerifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeBrandVerifications = () => {
    const initialData = googlePlaceBrands.map((googlePlaceBrand) => ({
      googlePlaceBrand,
      status: "loading" as BrandStatus,
    }));
    setBrandVerifications(initialData);
    verifyBrandsInParallel(initialData);
  };

  const verifyBrandsInParallel = async (
    initialData: BrandVerificationData[],
  ) => {
    if (!currentOrg?.id) return;

    initialData.forEach(async (brandData, index) => {
      try {
        const { bentoBrand, bentoContacts } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/google-place-brands/${brandData.googlePlaceBrand.id}/bento-brand`,
          "POST",
          {},
          {
            searchMetadata: {
              text_query: searchQuery,
            },
          },
        );

        const hasContacts = bentoContacts && bentoContacts.length > 0;
        const hasBentoBrand = !!bentoBrand;

        const updatedBrandData = {
          ...brandData,
          status:
            hasContacts && hasBentoBrand
              ? BrandStatus.contactsFound
              : BrandStatus.contactsNotFound,
          bentoBrand,
          bentoContacts,
        };

        setBrandVerifications((prev) =>
          prev.map((brand, i) => (i === index ? updatedBrandData : brand)),
        );
      } catch (error) {
        console.error(
          `Error verifying brand ${brandData.googlePlaceBrand.displayName}:`,
          error,
        );

        const updatedBrandData = {
          ...brandData,
          status: BrandStatus.contactsNotFound,
        };

        setBrandVerifications((prev) =>
          prev.map((brand, i) => (i === index ? updatedBrandData : brand)),
        );
      }
    });
  };

  const removeGooglePlaceBrand = (googlePlaceBrandId: number) => {
    setBrandVerifications((verifications) =>
      verifications.filter((v) => v.googlePlaceBrand.id !== googlePlaceBrandId),
    );
    setGooglePlaceBrands((googlePlaceBrands) =>
      googlePlaceBrands.filter((b) => b.id !== googlePlaceBrandId),
    );
    setBrandsError("");
  };

  const handleCreateCollection = async (overridePending: boolean) => {
    if (!currentOrg?.id) return;

    setNextLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/google-place-outreach-collections`,
        "POST",
        {},
        {
          collectionName: searchQuery,
          googlePlaceBrandIds: googlePlaceBrands.map(
            (googlePlace) => googlePlace.id,
          ),
          overridePending,
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (jsonMessage.field === "google_place_brand_ids") {
            if (jsonMessage.message.includes("processing")) {
              const stillProcessingNames = brandVerifications
                .filter((bV) => bV.status !== BrandStatus.contactsFound)
                .map((bV) => bV.googlePlaceBrand.displayName)
                .filter(Boolean)
                .join(", ");
              setConfirmDialogData({
                header: "Still Searching",
                subheader: `We're still looking for contacts${
                  stillProcessingNames
                    ? ` for these brands: ${stillProcessingNames}`
                    : ""
                }. Do you want to continue and exclude these brands from this email campaign?`,
                confirmText: "Yes",
                cancelText: "No",
                cancel: () => {
                  setConfirmDialogData(null);
                },
                confirm: () => {
                  handleCreateCollection(true);
                  setConfirmDialogData(null);
                },
              });
              return;
            } else if (jsonMessage.message.includes("no brands found")) {
              setConfirmDialogData({
                header: "No Contacts to Email",
                subheader: `We couldn't find any new contacts for the brands you selected — either there are no contacts available, or you've already emailed all of them before. Please choose different brands and try again.`,
                confirmText: "Try Again",
                confirm: () => {
                  handleClose();
                  setConfirmDialogData(null);
                },
              });
              return;
            }
          }

          if (jsonMessage.field === "date") {
            setBrandsError(
              `Unfortunately we cannot schedule so many emails at once. Can you reduce the number of brands. The ideal number is less than 200 brands. If you continue to face this error, please contact us at hello@onbento.com.`,
            );

            return;
          }

          if (jsonMessage.field === "paywall") {
            if (jsonMessage.message.includes("remaining this month")) {
              setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
            } else {
              setUpgradeDialogSource(UpgradeDialogSource.campaigns_limit);
            }
            return;
          }
        } else if (jsonMessage.message?.includes("Google integration")) {
          setOpenIntegrationDialog(true);
          return;
        }
        setAlert(jsonMessage.message, "error");
      } else {
        openCompleted(jsonMessage.campaign.id);
        handleClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobileScreen}
      maxWidth="lg"
    >
      <DialogTitle>
        <Typography variant="h4">Pitch to Brands</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              gutterBottom
              display="flex"
              alignItems="center"
              gap={1}
            >
              Brands{" "}
              <Typography
                component="span"
                color="textSecondary"
                variant="caption"
              >
                (Processed{" "}
                {
                  brandVerifications.filter(
                    (bV) => bV.status !== BrandStatus.loading,
                  ).length
                }
                /{googlePlaceBrands.length})
              </Typography>
            </Typography>
            {!!brandsError && (
              <Typography paragraph color="error" variant="caption">
                {brandsError}
              </Typography>
            )}
            <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
              {brandVerifications.map((brandData) => (
                <PendingItem
                  key={brandData.googlePlaceBrand.id}
                  brandData={brandData}
                  onDelete={removeGooglePlaceBrand}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleBack}>
          Back
        </Button>
        <LoadingButton
          loading={nextLoading}
          color="primary"
          variant="contained"
          disableElevation
          onClick={() => {
            handleCreateCollection(false);
          }}
        >
          Continue
        </LoadingButton>
      </DialogActions>

      {confirmDialogData && (
        <ConfirmDialog
          header={confirmDialogData.header}
          subheader={confirmDialogData.subheader}
          confirmText={confirmDialogData.confirmText}
          open={!!confirmDialogData}
          cancelText={confirmDialogData.cancelText}
          handleCancel={confirmDialogData.cancel}
          handleConfirm={confirmDialogData.confirm}
        />
      )}
    </Dialog>
  );
}
